import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Home, Hammer } from "lucide-react";
import { ForceLogo } from "../ForceLogo";
import EasyGuestVoice from "./EasyGuestVoice";

// Easy Mode landing shell (visitors, logged out). Same two giant buttons; both
// reveal the sign-in card, since an account is needed to post or apply.
export default function EasyLanding({ header, rightSlot }) {
    const [showAuth, setShowAuth] = useState(null);

    function pick(role) {
        setShowAuth(role);
    }

    // Dispatch AFTER AuthWindow has mounted inside the showAuth block, otherwise
    // the role-preselect event fires into thin air.
    useEffect(() => {
        if (!showAuth) return;
        const t = setTimeout(() => {
            window.dispatchEvent(new CustomEvent("force:start-signup", { detail: { role: showAuth } }));
        }, 200);
        return () => clearTimeout(t);
    }, [showAuth]);

    return (
        <div data-testid="easy-landing" className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-force-bg text-white">
            <div className="pointer-events-none fixed inset-x-0 top-0 h-96 opacity-70" style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(255,122,0,0.16), transparent 70%)" }} />
            {header}
            <main className="relative z-10 mx-auto flex w-full max-w-4xl flex-1 flex-col items-center justify-center px-[4vw] py-[2vh]">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex w-full flex-col items-center">
                    <ForceLogo layout="vertical" size={Math.round(Math.min(120, Math.max(64, Math.min(window.innerWidth, window.innerHeight) * 0.13)))} />
                    <h1 className="mt-[1.5vh] text-center font-display font-black tracking-tight text-[clamp(1.7rem,5vmin,3.6rem)]">
                        Let the <span className="text-orange-500">FORCE</span><br />assist you
                    </h1>
                    <p className="mt-[0.5vh] text-center font-semibold text-zinc-300 text-[clamp(0.95rem,2.8vmin,1.7rem)]">No fuss. Just two buttons.</p>

                    <div className="mt-[2.5vh] flex w-full flex-col gap-[2vh]">
                        <motion.button
                            data-testid="easy-landing-hire"
                            onClick={() => pick("homeowner")}
                            whileTap={{ scale: 0.97 }}
                            className="flex items-center gap-[2.5vw] rounded-3xl bg-gradient-to-r from-orange-500 to-amber-500 p-[clamp(0.9rem,3vmin,2rem)] text-left shadow-[0_20px_60px_-15px_rgba(255,122,0,0.6)]"
                        >
                            <span className="grid shrink-0 place-items-center rounded-2xl bg-zinc-950/90 h-[clamp(3.5rem,10vmin,7rem)] w-[clamp(3.5rem,10vmin,7rem)]"><Home className="h-[55%] w-[55%] text-orange-400" /></span>
                            <span>
                                <span className="block font-display font-black uppercase tracking-tight text-zinc-950 text-[clamp(1.5rem,4.5vmin,3rem)]">Find a worker</span>
                                <span className="block font-semibold text-zinc-900/80 text-[clamp(0.9rem,2.6vmin,1.5rem)]">I need something done at home</span>
                            </span>
                        </motion.button>
                        <motion.button
                            data-testid="easy-landing-work"
                            onClick={() => pick("worker")}
                            whileTap={{ scale: 0.97 }}
                            className="flex items-center gap-[2.5vw] rounded-3xl border-2 border-orange-500/60 bg-white/5 p-[clamp(0.9rem,3vmin,2rem)] text-left backdrop-blur transition-colors hover:bg-orange-500/10"
                        >
                            <span className="grid shrink-0 place-items-center rounded-2xl border border-orange-500/40 bg-orange-500/15 h-[clamp(3.5rem,10vmin,7rem)] w-[clamp(3.5rem,10vmin,7rem)]"><Hammer className="h-[55%] w-[55%] text-orange-400" /></span>
                            <span>
                                <span className="block font-display font-black uppercase tracking-tight text-[clamp(1.5rem,4.5vmin,3rem)]">Find work</span>
                                <span className="block font-semibold text-zinc-400 text-[clamp(0.9rem,2.6vmin,1.5rem)]">I want to earn money today</span>
                            </span>
                        </motion.button>
                    </div>

                    <div className="mt-[2vh] w-full">
                        <EasyGuestVoice onSignup={(role) => pick(role)} />
                    </div>

                    {showAuth && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-[4vh] w-full max-w-md" data-testid="easy-auth-wrap">
                            <p className="mb-3 text-center text-xl font-bold text-zinc-200">First, your free account — it takes one minute:</p>
                            {rightSlot}
                        </motion.div>
                    )}
                </motion.div>
            </main>
        </div>
    );
}
